import {
  getAgentConfigurationFromCdnByAgentId,
  getPricingsFromCdnByAgentId,
} from "@nemuru-eng/js-api-client/slim";
import { useContext, useMemo, useState } from "react";
import { useEffect } from "react";
import { AuthContext } from "../../../../../../contexts/AuthContext";
import { ProcessContext } from "../../../../../../contexts/ProcessContext";
import { LenderMap, LenderName } from "@nemuru-eng/js-api-client";

const EXCLUDED_LENDERS = [LenderName.FINONLINE];

export const useMultiLender = ({ loanConditions }) => {
  const {
    state: { agent },
  } = useContext(AuthContext);
  const {
    state: { loan },
  } = useContext(ProcessContext);

  const [pricingsFromCdn, setPricingsFromCdn] = useState(undefined);
  const [filteredPricings, setFilteredPricings] = useState(undefined);
  const [availableLenders, setAvailableLenders] = useState([]);
  const [allLenders, setAllLenders] = useState([]);

  useEffect(() => {
    if (!agent) return;
    let cancelled = false;
    (async () => {
      const pricings = await getPricingsFromCdnByAgentId({ agentId: agent.id });
      if (!cancelled) setPricingsFromCdn(pricings);
    })();
    return () => {
      cancelled = true;
    };
  }, [agent]);

  useEffect(() => {
    if (!loan || !pricingsFromCdn) return;
    let cancelled = false;

    (async () => {
      const config = await getAgentConfigurationFromCdnByAgentId({
        agentId: loan.agent_id,
      });

      const allowedLendersInLenderContext = [
        ...new Set(
          config.agentsInLenderContext
            .filter(
              (item) =>
                item.program === loan.loan_conditions.program.name &&
                item.product === loan.loan_conditions.product
            )
            .map((item) => item.lenderName)
        ),
      ];

      // Filter excluded lenders
      const filteredAllowedLendersInLenderContext =
        allowedLendersInLenderContext.filter(
          (lenderName) => !EXCLUDED_LENDERS.includes(lenderName)
        );

      const approvedLendersInMerchant = config.approvedLendersInMerchant;

      const allowedLenders = filteredAllowedLendersInLenderContext.filter(
        (lenderName) => approvedLendersInMerchant.includes(lenderName)
      );

      const allPricingsForProductAndProgram = [...pricingsFromCdn]
        .filter((pricing) => filterProgram(pricing, loan))
        .filter((pricing) => filterProduct(pricing, loan));

      if (!cancelled) {
        setAllLenders(
          getAvailableLendersInPricings(
            allPricingsForProductAndProgram,
            allowedLenders
          )
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loan, pricingsFromCdn]);

  useEffect(() => {
    if (!pricingsFromCdn || !loan || !loanConditions || !allLenders.length) {
      return;
    }

    const filtered = pricingsFromCdn
      .filter((pricing) => filterProgram(pricing, loan))
      .filter((pricing) => filterProduct(pricing, loan))
      .filter((pricing) => filterGracePeriod(pricing, loan))
      .filter((pricing) => filterSetupFee(pricing, loan))
      .filter((pricing) => filterPrincipal(pricing, loanConditions))
      .filter((pricing) => filterPeriod(pricing, loanConditions));

    setFilteredPricings(filtered);
    setAvailableLenders(getAvailableLendersInPricings(filtered, allLenders));
  }, [loanConditions, pricingsFromCdn, allLenders, loan]);

  const hasMultipleTin = () => {
    const selectedPeriod = loanConditions.period;
    const uniqueTinValues = new Set(
      filteredPricings
        .map((pricing) =>
          pricing.ranges.find(
            (range) =>
              range.rangePeriodMax >= selectedPeriod &&
              range.rangePeriodMin <= selectedPeriod
          )
        )
        .filter(Boolean)
        .map((range) => range.annualInterest)
    );

    return uniqueTinValues.size > 1;
  };

  const hasMultipleSetupFee = () => {
    const selectedPeriod = loanConditions.period;
    const uniqueSetupFeeValues = new Set(
      filteredPricings
        .map((pricing) =>
          pricing.ranges.find(
            (range) =>
              range.rangePeriodMax >= selectedPeriod &&
              range.rangePeriodMin <= selectedPeriod
          )
        )
        .filter(Boolean)
        .map((range) => range.setupFee.value)
    );

    return uniqueSetupFeeValues.size > 1;
  };

  const hasMultiplePricings = useMemo(() => {
    if (!filteredPricings || !filteredPricings.length) {
      return false;
    }

    if (hasMultipleTin()) {
      return true;
    }

    if (hasMultipleSetupFee()) {
      return true;
    }

    return false;
  }, [filteredPricings]);

  return {
    hasMultiplePricings,
    availableLenders,
    filteredPricings,
    allLenders,
  };
};

const getAvailableLendersInPricings = (pricings, allowedLenders) => {
  if (!pricings) return [];

  const allowedLenderIds = allowedLenders.map((lender) =>
    LenderMap.fromLenderName(lender)
  );

  const pricingsArray = Object.values(pricings);
  let lenders = [];

  for (const pricing of pricingsArray) {
    pricing.ranges.forEach((range) => {
      range.lenderIds.forEach((lenderId) => {
        if (!lenders.includes(lenderId)) {
          lenders.push(lenderId);
        }
      });
    });
  }

  return lenders
    .map((id) => {
      if (allowedLenderIds.includes(id)) {
        const lender = LenderMap.fromLenderId(id);
        return lender;
      }
    })
    .filter((lender) => lender !== undefined);
};

const filterProduct = (pricing, loan) => {
  return pricing.product === loan.loan_conditions.product;
};

const filterProgram = (pricing, loan) => {
  return pricing.program === loan.loan_conditions.program.name;
};

const filterGracePeriod = (pricing, loan) =>
  (loan.loan_conditions.loan_calculator.grace_period > 0 &&
    pricing.ranges.some((range) => range.gracePeriod > 0)) ||
  (loan.loan_conditions.loan_calculator.grace_period === 0 &&
    !pricing.ranges.some((range) => range.rangePeriod > 0));

const filterSetupFee = (pricing, loan) =>
  (loan.loan_conditions.loan_calculator.setup_fee?.fee_amount.amount > 0 &&
    pricing.ranges.some((range) => range.setupFee?.value > 0)) ||
  (loan.loan_conditions.loan_calculator.setup_fee?.fee_amount.amount === 0 &&
    !pricing.ranges.some((range) => range.setupFee?.value > 0));

const filterPrincipal = (pricing, loanConditions) => {
  if (!loanConditions.principalAmount) return false;
  return pricing.ranges.some(
    (range) =>
      range.rangePrincipalMin.value <= loanConditions.principalAmount &&
      range.rangePrincipalMax.value >= loanConditions.principalAmount
  );
};

const filterPeriod = (pricing, loanConditions) => {
  if (!loanConditions.period) return false;
  return pricing.ranges.some(
    (range) =>
      range.rangePeriodMin <= loanConditions.period &&
      range.rangePeriodMax >= loanConditions.period
  );
};
